// Auto-Generator Überschreib-Verlauf — Vorher/Nachher-Diff für überschriebene
// Zeiterfassungs-Einträge (Feb 2026).
import { useEffect, useState } from "react";
import { History, Calendar, User, ArrowRight, Search } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

function fmtDate(s) {
  if (!s) return "—";
  return s.slice(0, 10);
}
function fmtDateTime(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleString("de-AT", { dateStyle: "short", timeStyle: "short" }); }
  catch { return s; }
}

function DiffCell({ prev, next: nextVal, label, suffix = "" }) {
  const changed = String(prev ?? "") !== String(nextVal ?? "");
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[9px] uppercase tracking-wider text-zinc-500">{label}</span>
      <div className="flex items-center gap-1.5 font-mono text-xs">
        <span className={changed ? "text-rose-300 line-through" : "text-zinc-500"}>
          {prev || "—"}{suffix}
        </span>
        <ArrowRight className="w-3 h-3 text-zinc-600" />
        <span className={changed ? "text-emerald-300 font-semibold" : "text-zinc-400"}>
          {nextVal || "—"}{suffix}
        </span>
      </div>
    </div>
  );
}

function BreakTypeCount(breaks) {
  const out = { SO: 0, RP: 0, LP: 0 };
  for (const b of breaks || []) {
    const t = (b.type || "RP").toUpperCase();
    if (out[t] !== undefined) out[t] += 1;
  }
  return out;
}

function OverwriteRow({ row }) {
  const prev = row.prev_entry || {};
  const next = row.new_entry || {};
  const prevBreaks = BreakTypeCount(prev.breaks);
  const nextBreaks = BreakTypeCount(next.breaks);
  const breakSummary = (b) => `${b.SO} SAZ · ${b.RP} RP${b.LP ? ` · ${b.LP} LP` : ""}`;

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4" data-testid={`overwrite-row-${row.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2 text-sm">
          <Calendar className="w-3.5 h-3.5 text-amber-400" />
          <span className="font-display font-semibold text-zinc-50">{fmtDate(row.date)}</span>
          <span className="text-zinc-600">·</span>
          <User className="w-3.5 h-3.5 text-zinc-500" />
          <span className="text-zinc-300">{row.employee_name}</span>
          <span className="text-zinc-600">·</span>
          <span className="text-[10px] uppercase tracking-wider text-amber-300 bg-amber-500/10 px-1.5 py-0.5 rounded border border-amber-500/20">
            {row.rule_code}
          </span>
        </div>
        <div className="text-[10px] text-zinc-500">
          {fmtDateTime(row.overwritten_at)} · {row.overwritten_by_email || "—"}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
        <DiffCell label="Arbeitsbeginn" prev={prev.work_start} next={next.work_start} />
        <DiffCell label="Arbeitsende" prev={prev.work_end} next={next.work_end} />
        <DiffCell label="Lenkbeginn" prev={prev.driving_start} next={next.driving_start} />
        <DiffCell label="Lenkende" prev={prev.driving_end} next={next.driving_end} />
        <DiffCell label="Start-KM" prev={prev.start_km} next={next.start_km} />
        <DiffCell label="End-KM" prev={prev.end_km} next={next.end_km} />
        <DiffCell label="Pause (min)" prev={prev.break_minutes} next={next.break_minutes} />
        <DiffCell label="Pausen-Typen" prev={breakSummary(prevBreaks)} next={breakSummary(nextBreaks)} />
      </div>
    </div>
  );
}

export default function AutoGenOverwrites() {
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [busy, setBusy] = useState(false);
  const today = new Date();
  const firstOfMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-01`;
  const [filters, setFilters] = useState({
    employee_id: "",
    date_from: firstOfMonth,
    date_to: "",
  });

  async function load() {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (filters.employee_id) params.set("employee_id", filters.employee_id);
      if (filters.date_from) params.set("date_from", filters.date_from);
      if (filters.date_to) params.set("date_to", filters.date_to);
      params.set("limit", "200");
      const { data } = await api.get(`/auto-gen/overwrites?${params.toString()}`);
      setRows(data.overwrites || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Konnte Überschreib-Verlauf nicht laden");
    } finally { setBusy(false); }
  }

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/employees");
        setEmployees((data || []).filter(e => e.role === "fahrer"));
      } catch { /* noop */ }
    })();
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const INP = "px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 focus:border-amber-500/60 outline-none";

  return (
    <div className="px-6 md:px-8 py-6 space-y-6" data-testid="auto-gen-overwrites-page">
      <div>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
          <History className="w-3 h-3" /> Auto-Generator · Audit
        </div>
        <h1 className="font-display text-2xl font-bold tracking-tight text-zinc-50 mt-1">
          Überschreib-Verlauf
        </h1>
        <p className="text-xs text-zinc-400 mt-2 max-w-2xl">
          Jeder Eintrag, der vom Auto-Generator überschrieben wurde, wird hier mit
          Vorher- und Nachher-Werten protokolliert. Hilft beim Nachvollziehen, was
          sich nach einem Lauf geändert hat.
        </p>
      </div>

      <section className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-[10px] uppercase tracking-wider text-zinc-500">Fahrer</label>
          <select className={INP} value={filters.employee_id} data-testid="overwrites-employee-filter"
            onChange={e => setFilters(p => ({ ...p, employee_id: e.target.value }))}>
            <option value="">— alle —</option>
            {employees.map(e => (
              <option key={e.id} value={e.id}>{e.last_name} {e.first_name}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[10px] uppercase tracking-wider text-zinc-500">Datum von</label>
          <input type="date" className={INP} value={filters.date_from}
            onChange={e => setFilters(p => ({ ...p, date_from: e.target.value }))}/>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[10px] uppercase tracking-wider text-zinc-500">Datum bis</label>
          <input type="date" className={INP} value={filters.date_to}
            onChange={e => setFilters(p => ({ ...p, date_to: e.target.value }))}/>
        </div>
        <button onClick={load} disabled={busy} data-testid="overwrites-search-btn"
          className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded text-sm font-medium disabled:opacity-50">
          <Search className="w-4 h-4" />
          {busy ? "Lade …" : "Suchen"}
        </button>
        <div className="ml-auto text-xs text-zinc-500">
          {rows.length} Treffer
        </div>
      </section>

      <div className="space-y-3">
        {rows.length === 0 && !busy && (
          <div className="text-center text-sm text-zinc-500 py-12 bg-zinc-900 border border-zinc-800 rounded-lg">
            Keine überschriebenen Einträge im gewählten Zeitraum.
          </div>
        )}
        {rows.map(r => <OverwriteRow key={r.id} row={r} />)}
      </div>
    </div>
  );
}
